import { getMensagensAcompanhamento, createMensagem, updateMensagem, deleteMensagem } from "@spark/services/acompanhamentos";
import { useState, useCallback, useEffect } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
/**
 * Hook especializado para gerenciar mensagens de um acompanhamento
 * @param {string} acompanhamentoId - ID do acompanhamento
 * @returns {object} - Estados e funções para gerenciar mensagens
 */

export const useMensagens = acompanhamentoId => {
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  }); // Carregar mensagens

  const loadMensagens = useCallback(async () => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('💬 Carregando mensagens para acompanhamento:', acompanhamentoId);
      const params = new URLSearchParams();
      if (pagination.pageIndex > 0) params.append('page', pagination.pageIndex + 1);
      if (pagination.pageSize !== 10) params.append('limit', pagination.pageSize); // Adicionar filtros

      if (filters.search) params.append('search', filters.search);
      if (filters.usuario) params.append('usuario', filters.usuario);
      if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
      if (filters.dataFim) params.append('dataFim', filters.dataFim);
      const response = await getMensagensAcompanhamento(acompanhamentoId);
      let mensagensData = [];

      if (response?.data?.mensagens) {
        mensagensData = response.data.mensagens;
      } else if (response?.data && Array.isArray(response.data)) {
        mensagensData = response.data;
      }

      console.log('📥 Mensagens carregadas:', mensagensData.length);
      setMensagens(mensagensData);
      return mensagensData;
    } catch (err) {
      console.error('❌ Erro ao carregar mensagens:', err);
      setError(err); // Erro já tratado pelo interceptor global

      return [];
    } finally {
      setLoading(false);
    }
  }, [acompanhamentoId, pagination, filters]); // Efeito para carregar mensagens quando o acompanhamentoId muda

  useEffect(() => {
    if (acompanhamentoId) {
      loadMensagens();
    }
  }, [acompanhamentoId, loadMensagens]); // Criar nova mensagem

  const createMensagemData = useCallback(async formData => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('✨ Criando nova mensagem');
      const response = await createMensagem(acompanhamentoId, formData);

      if (response && response.success) {
        notification.success('Mensagem enviada com sucesso!');
        await loadMensagens(); // Recarregar lista

        return response;
      } else {
        const errorMessage = response?.message || 'Erro interno do servidor';
        notification.error('Erro ao enviar mensagem', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('❌ Erro ao criar mensagem:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [acompanhamentoId, loadMensagens]); // Atualizar mensagem existente

  const updateMensagemData = useCallback(async (mensagemId, payload) => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('📝 Atualizando mensagem:', mensagemId);
      await updateMensagem(acompanhamentoId, mensagemId, payload);
      notification.success('Mensagem atualizada com sucesso!');
      await loadMensagens(); // Recarregar lista
    } catch (err) {
      console.error('❌ Erro ao atualizar mensagem:', err);
      setError(err); // Erro já tratado pelo interceptor global

      throw err;
    } finally {
      setLoading(false);
    }
  }, [acompanhamentoId, loadMensagens]); // Deletar mensagem

  const deleteMensagemData = useCallback(async mensagemId => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('🗑️ Deletando mensagem:', mensagemId);
      await deleteMensagem(acompanhamentoId, mensagemId);
      notification.success('Mensagem excluída com sucesso!');
      await loadMensagens(); // Recarregar lista
    } catch (err) {
      console.error('❌ Erro ao deletar mensagem:', err);
      setError(err); // Erro já tratado pelo interceptor global

      throw err;
    } finally {
      setLoading(false);
    }
  }, [acompanhamentoId, loadMensagens]); // Atualizar filtros

  const updateFilters = useCallback(newFilters => {
    setFilters(newFilters);
    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize
    });
  }, [pagination.pageSize]); // Atualizar paginação

  const updatePagination = useCallback(newPagination => {
    setPagination(newPagination);
  }, []); // Reset dos estados

  const resetState = useCallback(() => {
    setMensagens([]);
    setError(null);
    setLoading(false);
    setFilters({});
    setPagination({
      pageIndex: 0,
      pageSize: 10
    });
  }, []);
  return {
    // Estados
    mensagens,
    loading,
    error,
    filters,
    pagination,
    // Ações
    loadMensagens,
    createMensagem: createMensagemData,
    updateMensagem: updateMensagemData,
    deleteMensagem: deleteMensagemData,
    updateFilters,
    updatePagination,
    resetState
  };
};
